import { describe, it, expect, afterEach } from '@jest/globals';
import { createPackageSettingsTransform } from './createPackageSettingsTransform.js';
import { PackageDefinitions } from '@ms-cloudpack/package-utilities';
import { createTestFileStructure, removeTempDir } from '@ms-cloudpack/test-utilities';
import type {
  AppConfig,
  CloudpackConfig,
  GeneratedConfig,
  PackageDefinitionTransform,
  PackageJson,
  PackageJsonCloudpackMeta,
} from '@ms-cloudpack/common-types';
import { readConfig } from '../readConfig/readConfig.js';
import { slash } from '@ms-cloudpack/path-string-parsing';
import { builtInSettings } from '../builtInSettings/builtInSettings.js';

describe('createPackageSettingsTransform', () => {
  let testPath = '';

  afterEach(async () => {
    testPath && (await removeTempDir(testPath));
    testPath = '';
  });

  async function transformWrapper(
    config?: Partial<CloudpackConfig>,
    /** Run this before the package settings transform */
    preTransform?: PackageDefinitionTransform,
  ): Promise<PackageJson | undefined> {
    if (!testPath) {
      throw new Error('testPath must be set before calling this function.');
    }

    // eslint-disable-next-line no-restricted-syntax -- testing the transform
    const packages = new PackageDefinitions();
    let definition = await packages.get(testPath, { disableTransforms: true });
    definition = (await preTransform?.({ definition, packagePath: testPath, packages })) || definition;

    const transform = createPackageSettingsTransform({
      appPath: testPath,
      mode: 'library',
      generated: {},
      ...config,
    });

    // slash-normalize the path like PackageDefinitions does (not doing this allowed a Windows bug to slip through)
    return transform({ definition, packagePath: slash(testPath), packages });
  }

  it('preserves extra properties', async () => {
    const definition: PackageJson = {
      name: 'test',
      version: '1.0.0',
      description: 'awesome package',
      devDependencies: { foo: '1.0.0' },
    };
    testPath = await createTestFileStructure({ 'package.json': definition });

    const result = await transformWrapper();
    expect(definition.cloudpack).toBeUndefined();
    expect(result).toEqual({
      ...definition,
      exports: {},
      cloudpack: { mode: 'library' },
    });
  });

  it('includes built-in package settings', async () => {
    // verify this package still has built-in settings
    const asapSettings = builtInSettings['asap']?.['^2.0.0']?.packageSettings;
    expect(asapSettings).toBeTruthy();

    const definition: PackageJson = { name: 'asap', version: '2.0.0' };
    testPath = await createTestFileStructure({ 'package.json': definition });

    const result = await transformWrapper();
    expect(result).toBeDefined();
    expect(result?.cloudpack).toEqual({
      mode: 'library',
      ...asapSettings,
    });
  });

  it('preserves cloudpack properties set by previous transforms', async () => {
    const definition: PackageJson = { name: 'test', version: '1.0.0' };
    testPath = await createTestFileStructure({ 'package.json': definition });

    // Basic version of what getNonSemverTransform does
    const preTransform: PackageDefinitionTransform = ({ definition: def }) => {
      def.cloudpack ??= {} as PackageJsonCloudpackMeta;
      def.cloudpack.patchFilePath = 'patch';
      return def;
    };

    const result = await transformWrapper(undefined, preTransform);
    expect(result).toBeDefined();
    expect(result?.cloudpack).toEqual({
      mode: 'library',
      patchFilePath: 'patch',
    });
  });

  describe('exports', () => {
    it('generates a default exports map if no entry points are specified', async () => {
      const definition: PackageJson = { name: 'test', version: '1.0.0' };
      testPath = await createTestFileStructure({
        'package.json': definition,
        'lib/index.js': '',
      });

      const result = await transformWrapper();
      expect(result).toBeDefined();
      expect(result?.exports).toEqual({ '.': './lib/index.js' });
    });

    it('includes package main entry along with generated exports', async () => {
      const definition: PackageJson = { name: 'test', version: '1.0.0', main: './lib/index.js' };
      testPath = await createTestFileStructure({
        'package.json': definition,
        'lib/index.js': '',
        'lib/foo.js': '',
      });

      const result = await transformWrapper({
        generated: {
          packageSettings: [{ match: { name: 'test', version: '^1' }, exports: { './foo': './lib/foo.js' } }],
        },
      });
      expect(result).toBeDefined();
      expect(result?.exports).toEqual([
        {
          '.': './lib/index.js',
        },
        {
          './foo': './lib/foo.js',
        },
      ]);
    });

    it('includes package module entry along with generated exports', async () => {
      const definition: PackageJson = { name: 'test', version: '1.0.0', module: './lib/index.js' };
      testPath = await createTestFileStructure({
        'package.json': definition,
        'lib/index.js': '',
      });

      const result = await transformWrapper({
        generated: {
          packageSettings: [{ match: { name: 'test', version: '^1' }, exports: { './foo': './src/foo.js' } }],
        },
      });
      expect(result).toBeDefined();
      expect(result?.exports).toEqual([
        {
          '.': { import: './lib/index.js' },
        },
        {
          './foo': './src/foo.js',
        },
      ]);
    });

    it('returns exports as-is if specified in definition', async () => {
      const definition: PackageJson = { name: 'test', version: '1.0.0', exports: './foo.js' };
      testPath = await createTestFileStructure({ 'package.json': definition });

      const result = await transformWrapper();
      expect(result).toBeDefined();
      expect(result?.exports).toEqual('./foo.js');
    });

    it('respects null exports from definition', async () => {
      const definition: PackageJson = { name: 'test', version: '1.0.0', exports: null };
      testPath = await createTestFileStructure({
        'package.json': definition,
        'index.js': '', // this file should not be automatically picked up
      });

      const result = await transformWrapper();
      expect(result).toBeDefined();
      expect(result?.exports).toBeNull();
    });

    it('includes generated exports even if exports in definition are null', async () => {
      const definition: PackageJson = { name: 'test', version: '1.0.0', exports: null };
      testPath = await createTestFileStructure({
        'package.json': definition,
        'index.js': '', // this file should not be automatically picked up
      });

      const result = await transformWrapper({
        generated: { packageSettings: [{ match: 'test', exports: { './foo': './foo' } }] },
      });
      expect(result).toBeDefined();
      expect(result?.exports).toEqual({ './foo': './foo' });
    });

    it('overrides existing package exports if package settings are specified', async () => {
      const definition: PackageJson = { name: 'test', version: '1.0.0', exports: { './bar': './src/bar.js' } };
      testPath = await createTestFileStructure({ 'package.json': definition });

      const result = await transformWrapper({
        packageSettings: [{ match: { name: 'test' }, exports: { './foo': './src/foo.js' } }],
      });
      expect(result).toBeDefined();
      expect(result?.exports).toEqual({
        './foo': './src/foo.js',
      });
    });

    it('respects null exports from package settings', async () => {
      // Exports might be set to null in package settings if we want to remove them.
      const definition: PackageJson = {
        name: 'test',
        version: '1.0.0',
        exports: { './testStuff': './src/testStuff.js' },
      };
      testPath = await createTestFileStructure({ 'package.json': definition });

      const result = await transformWrapper({
        packageSettings: [{ match: { name: 'test' }, exports: null }],
      });
      expect(result).toBeDefined();
      expect(result?.exports).toBeNull();
    });

    it('returns only route exports for app packages', async () => {
      const definition: PackageJson = {
        name: 'test',
        version: '1.0.0',
        exports: {
          '.': './lib/index.js',
          './foo': './lib/foo.js',
        },
      };
      const rawConfig: AppConfig = {
        routes: [{ match: '/', entry: './lib/bar.js' }],
      };
      const rawGeneratedConfig: GeneratedConfig = {
        packageSettings: [{ match: 'test', exports: { './baz': './lib/baz.js' } }],
      };
      testPath = await createTestFileStructure({
        'package.json': definition,
        'src/index.ts': '//',
        'src/foo.ts': '//',
        'src/bar.ts': '//',
        'src/baz.ts': '//',
        'cloudpack.config.json': JSON.stringify(rawConfig),
        'cloudpack.generated.json': JSON.stringify(rawGeneratedConfig),
      });

      // Start with a realistic config object (including route processing) by reading from disk
      const result = await transformWrapper(await readConfig(testPath));
      expect(result).toBeDefined();
      expect(result?.exports).toEqual({
        './lib/bar.js': {
          browser: {
            default: './lib/bar.js',
            source: './src/bar.ts',
          },
        },
      });
    });

    it('converts serverEntry to a node export', async () => {
      const definition: PackageJson = {
        name: 'test',
        version: '1.0.0',
        exports: {
          '.': './lib/index.js',
        },
      };
      const rawConfig: AppConfig = {
        routes: [{ match: '/', entry: './lib/bar.js', serverEntry: './lib/bar.server.js' }],
      };
      testPath = await createTestFileStructure({
        'package.json': definition,
        'lib/index.js': '//',
        'lib/bar.js': '//',
        'lib/bar.server.js': '//',
        'cloudpack.config.json': JSON.stringify(rawConfig),
      });

      const result = await transformWrapper(await readConfig(testPath));

      expect(result).toBeDefined();
      expect(result?.exports).toEqual({
        './lib/bar.js': {
          browser: './lib/bar.js',
        },
        './lib/bar.server.js': {
          node: './lib/bar.server.js',
        },
      });
    });

    it('respects serverEntry even if there is no entry', async () => {
      const definition: PackageJson = {
        name: 'test',
        version: '1.0.0',
        exports: {
          '.': './lib/index.js',
        },
      };
      const rawConfig: AppConfig = {
        routes: [{ match: '/', serverEntry: './lib/bar.server.js' }],
      };
      testPath = await createTestFileStructure({
        'package.json': definition,
        'lib/index.js': '//',
        'lib/bar.server.js': '//',
        'cloudpack.config.json': JSON.stringify(rawConfig),
      });

      const result = await transformWrapper(await readConfig(testPath));

      expect(result).toBeDefined();
      expect(result?.exports).toEqual({
        './lib/bar.server.js': {
          node: './lib/bar.server.js',
        },
      });
    });

    it('expands wildcard and conditional patterns in user packageSettings exports', async () => {
      const definition: PackageJson = { name: 'test', version: '1.0.0' };
      testPath = await createTestFileStructure({
        'package.json': definition,
        'dist/foo.js': '',
        'dist/bar.js': '',
        'dist/ignore.txt': '',
        'feature/a.mjs': '',
        'feature/a.cjs': '',
        'feature/b.mjs': '',
        'feature/b.cjs': '',
      });

      const result = await transformWrapper({
        packageSettings: [
          {
            match: { name: 'test' },
            exports: {
              './*': './dist/*',
              './feature/*': { import: './feature/*.mjs', require: './feature/*.cjs' },
            },
          },
        ],
      });

      expect(result).toBeDefined();
      expect(result?.exports).toEqual({
        './foo.js': './dist/foo.js',
        './bar.js': './dist/bar.js',
        './ignore.txt': './dist/ignore.txt',
        './feature/a': { import: './feature/a.mjs', require: './feature/a.cjs' },
        './feature/b': { import: './feature/b.mjs', require: './feature/b.cjs' },
      });
    });
  });

  describe('dependencies', () => {
    it('adds dependencies based on includedDependencies', async () => {
      const definition: PackageJson = {
        name: 'test',
        version: '1.0.0',
        dependencies: { bbb: '1.0.0', ddd: '2.0.0' },
        devDependencies: { ccc: '3.0.0', eee: '1.0.0' },
        peerDependencies: { fff: '4.0.0', ggg: '1.0.0' },
        optionalDependencies: { hhh: '5.0.0', iii: '1.0.0' },
      };
      testPath = await createTestFileStructure({ 'package.json': definition });

      const result = await transformWrapper({
        packageSettings: [{ match: { name: 'test' }, includedDependencies: ['aaa', 'bbb', 'ccc', 'fff', 'hhh'] }],
      });
      expect(result).toBeDefined();
      expect(result?.dependencies).toEqual({
        aaa: '*', // unknown extra dep
        bbb: '1.0.0', // preserved from deps
        ddd: '2.0.0', // preserved from deps
        ccc: '3.0.0', // from dev deps
        hhh: '5.0.0', // from optional deps
      });
      expect(result?.peerDependencies).toEqual({
        fff: '4.0.0', // preserved from peer deps
        ggg: '1.0.0', // preserved from peer deps
      });
      // dev and optional should be unmodified
      expect(result?.devDependencies).toEqual(definition.devDependencies);
      expect(result?.optionalDependencies).toEqual(definition.optionalDependencies);
    });

    it('removes dependencies based on excludedDependencies', async () => {
      const definition: PackageJson = {
        name: 'test',
        version: '1.0.0',
        dependencies: { foo: '1.0.0', bar: '1.0.0', qux: '1.0.0' },
        peerDependencies: { baz: '1.0.0', quux: '1.0.0' },
      };
      testPath = await createTestFileStructure({ 'package.json': definition });

      const result = await transformWrapper({
        packageSettings: [{ match: { name: 'test' }, excludedDependencies: ['foo', 'bar', 'baz'] }],
      });
      expect(result).toBeDefined();
      expect(result?.dependencies).toEqual({ qux: '1.0.0' });
      expect(result?.peerDependencies).toEqual({ quux: '1.0.0' });
    });

    it('replaces includedDependencies: "$devDependencies" with actual dependencies', async () => {
      const definition: PackageJson = {
        name: 'test',
        version: '1.0.0',
        dependencies: { foo: '1.0.0', bar: '1.0.0' },
        devDependencies: { baz: '1.0.0', qux: '1.0.0' },
      };
      testPath = await createTestFileStructure({ 'package.json': definition });

      const result = await transformWrapper({
        packageSettings: [{ match: { name: 'test' }, includedDependencies: ['$devDependencies'] }],
      });
      expect(result).toBeDefined();
      expect(result?.dependencies).toEqual({
        ...definition.devDependencies,
        ...definition.dependencies,
      });
      // originals are unmodified
      expect(result?.devDependencies).toEqual(definition.devDependencies);
    });

    it('replaces excludedDependencies: "$peerDependencies" with actual dependencies', async () => {
      const definition: PackageJson = {
        name: 'test',
        version: '1.0.0',
        dependencies: { foo: '1.0.0', bar: '1.0.0' },
        peerDependencies: { baz: '1.0.0', qux: '1.0.0' },
        peerDependenciesMeta: { qux: { optional: true } },
      };
      testPath = await createTestFileStructure({ 'package.json': definition });

      const result = await transformWrapper({
        packageSettings: [{ match: { name: 'test' }, excludedDependencies: ['$peerDependencies'] }],
      });
      expect(result).toBeDefined();
      expect(result?.dependencies).toEqual(definition.dependencies);
      expect(result?.peerDependencies).toBeUndefined();
      expect(result?.peerDependenciesMeta).toBeUndefined();
    });
  });
});
